import React, { FC, useState } from 'react';
import { useData } from '@hooks/useData';
import { GetMyStoresOrderDetailSuccessResponse } from '@services/useGetMyStoresOrderDetail';
import { OrderDetailManagementTable } from '../../OrderDetailManagementTable';
import { useTranslation } from 'react-i18next';
import { CancelLockerCompartmentReservationModal } from '../../modals/CancelLockerCompartmentBookingModal';
import { Typography } from '@components/atoms/Typography';

type Delivery = NonNullable<GetMyStoresOrderDetailSuccessResponse['data']>['delivery'];

interface DeliveryTableProps {
    orderUuid: string;
    storeUuid: string;
}

export const DeliveryTable: FC<DeliveryTableProps> = ({ orderUuid, storeUuid }) => {
    const { myStoresOrderDetail, isLoadingMyStoresOrderDetail, isLoadingCancelLockerCompartmentReservation } = useData();
    const { t } = useTranslation();

    const translations = {
        address: t('manage-order-detail-delivery-table-component.header.address'),
        booking: t('manage-order-detail-delivery-table-component.header.booking'),
        products: t('manage-order-detail-delivery-table-component.header.products'),
        noBookings: t('manage-order-detail-delivery-table-component.header.no-bookings'),
    };

    // if null, hide modal
    const [cancelBookingModal, setCancelBookingModal] = useState<string | null>(null);

    const delivery = !isLoadingMyStoresOrderDetail ? myStoresOrderDetail?.data?.delivery || null : null;

    const deliveries = getDeliveries(delivery);

    const showConfirmCancelDeliveryModal = (deliveryId: string) => {
        setCancelBookingModal(deliveryId);
    };

    return (
        <>
            {delivery?.locker ? (
                <>
                    <OrderDetailManagementTable
                        withActions={false}
                        header={[{ text: translations.address }]}
                        content={[
                            {
                                cells: [
                                    {
                                        content: (
                                            <>
                                                <Typography classes="mb-3" fontSize="16">
                                                    <>{delivery.locker?.name || ''}</>
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.address}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.postalCode}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.location}
                                                </Typography>
                                            </>
                                        ),
                                    },
                                ],
                                options: [],
                            },
                        ]}
                    />
                    <OrderDetailManagementTable
                        withActions={true}
                        header={[{ text: translations.booking }, { text: translations.products, hideOnMobile: true }]}
                        content={
                            deliveries.length > 0
                                ? deliveries.map((item) => {
                                      return {
                                          cells: [
                                              {
                                                  content: <b>{item.name || ''}</b>,
                                              },
                                              {
                                                  content: (
                                                      <>
                                                          {item.products?.map((product) => (
                                                              <p style={{ whiteSpace: 'pre-line' }}>{product}</p>
                                                          ))}
                                                      </>
                                                  ),
                                              },
                                          ],
                                          options: [
                                              {
                                                  icon: 'fa-light fa-xmark',
                                                  action: () =>
                                                      !isLoadingCancelLockerCompartmentReservation &&
                                                      showConfirmCancelDeliveryModal(item.id),
                                              },
                                          ],
                                      };
                                  })
                                : [
                                      {
                                          cells: [
                                              {
                                                  content: (
                                                      <Typography classes="mb-1" fontSize="14">
                                                          {translations.noBookings}
                                                      </Typography>
                                                  ),
                                              },
                                          ],
                                          options: [],
                                      },
                                  ]
                        }
                    />
                    <CancelLockerCompartmentReservationModal
                        orderUuid={orderUuid}
                        storeUuid={storeUuid}
                        reservationId={cancelBookingModal}
                        show={cancelBookingModal !== null}
                        onClose={() => setCancelBookingModal(null)}
                    />
                </>
            ) : (
                <OrderDetailManagementTable
                    withActions={false}
                    header={[{ text: translations.address }]}
                    content={deliveries.map((item) => {
                        return {
                            cells: [
                                {
                                    content: (
                                        <>
                                            <b>{item.name || ''}</b>
                                            <br />
                                            <p style={{ whiteSpace: 'pre-line' }}>{item.address}</p>
                                        </>
                                    ),
                                },
                            ],
                            options: [],
                        };
                    })}
                />
            )}
        </>
    );
};

const getDeliveries: (delivery: Delivery | null) => { id: string; name?: string; address?: string; products?: string[] }[] = (
    delivery
) => {
    if (delivery?.shipToPickupSpot) {
        return [
            {
                id: '1',
                name: delivery.shipToPickupSpot.name,
                address: delivery.shipToPickupSpot.address || '',
            },
        ];
    }

    if (delivery?.pickupAtStore) {
        return delivery.pickupAtStore.map((item, index) => ({
            id: `${index}`,
            name: item.name,
            address: item.address || '',
        }));
    }

    if (delivery?.shipToAddress) {
        const shippingAddress = delivery?.shipToAddress;

        const shippingAddressText = shippingAddress
            ? `${shippingAddress?.recipient_first_name} ${shippingAddress?.recipient_last_name}` +
              `\nNIF: ${shippingAddress?.fiscal_number || '-'}` +
              `\n${shippingAddress.address_line1}` +
              `\n${shippingAddress.address_line2}` +
              `\n${shippingAddress.postal_code}` +
              `\n${shippingAddress.municipality}` +
              `\n${shippingAddress.region?.name}`
            : '';

        return [
            {
                id: '1',
                address: shippingAddressText,
            },
        ];
    }

    if (delivery?.locker) {
        return (delivery.lockerReservations || []).map((reservation) => ({
            id: reservation.reservation_id || '',
            name: reservation.reservation_number || '',
            products: reservation.orderItems?.map((item) => {
                return item.quantity + ' x ' + item.name + (item.attributes ? ', ' + item.attributes?.join(', ') : '');
            }),
        }));
    }

    return [];
};
